import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Confirm, Notify } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { FaArrowLeft, FaBan, FaPlay, FaSyncAlt, FaTrashAlt } from 'react-icons/fa'
import api from '../../../../helpers/apiRequester'
import { CollapsibleSection } from '../../omnibusShared/collapsibleSection'
import styles from '../shared/mhlShared.module.scss'
import { Pill, SourceTabs } from '../shared/SourceTabs'
import type { AudiogramDoc, EsignDocs, MhlCampaign, MhlClientRecord, MhlLeadRecord, PresignedDoc } from '../shared/mhlTypes'
import {
    AUDIOLOGY_LONG_LABELS,
    agentDisplay,
    audiologyTone,
    esignLabel,
    esignTone,
    formatDate,
    formatDateTime,
    formatMoney,
    fullName,
    quantumLabel,
    reviewLabel,
    reviewTone,
    yesNo,
} from '../shared/mhlFormatting'
import { useMhlAccess } from '../shared/useMhlAccess'
import { ReviewPanel } from './ReviewPanel'
import { AudiogramViewer } from './AudiogramViewer'
import { IdentityDocumentsSection } from './IdentityDocumentsSection'
import { AudiologyAssessment, formatAssessedDb } from './AudiologyAssessment'
import { ClientDetailsSection } from './ClientDetailsSection'
import { ServiceRecordsSection } from './ServiceRecordsSection'
import { EsignSendButtons } from '../components/EsignSendButtons/EsignSendButtons'
import { ChaseMessagesTable } from '../MilitaryHearingLossWebsiteVisitors/ChaseMessagesTable'
import { colors, InfoTip } from '../../../../ui'

const timelineTone = (type: string) => {
    if (type === 'approved' || type === 'signed') return 'good'
    if (type === 'rejected' || type === 'chase_stopped') return 'bad'
    if (type === 'submitted' || type === 'audiogram' || type === 'chase_sent') return 'warn'
    if (type === 'assessed' || type === 'link_sent' || type === 'link_opened' || type === 'proceed') return 'info'
    return ''
}

type EsignDocState = 'unsigned' | 'signed'
type DocTab = EsignDocState | 'identity' | 'other'
type EsignDocRow = { key: string; label: string; caseRef: string; doc: PresignedDoc; state: EsignDocState; fileName?: string }

const toPreviewDoc = (row: EsignDocRow): AudiogramDoc => {
    const fileName = row.fileName || row.label
    const mime = row.doc.contentType || 'application/pdf'
    return {
        ...row.doc,
        lead_id: 0,
        case_ref: row.caseRef,
        fileName,
        mime,
        uploadedAt: row.doc.lastModified,
        uploadedBy: null,
    }
}

const esignDownloadRows = (d: EsignDocs): EsignDocRow[] => {
    const packRows: EsignDocRow[] = (d.pack || []).map((doc) => ({
        key: doc.key || `${d.case_ref}-${doc.id}-${doc.state}-${doc.fileName}`,
        label: doc.title,
        caseRef: d.case_ref,
        doc,
        state: doc.state,
        fileName: doc.fileName,
    }))
    const extras: EsignDocRow[] = []
    if (d.unsigned) {
        extras.push({
            key: d.unsigned.key || `${d.case_ref}-unsigned`,
            label: 'E-sign pack',
            caseRef: d.case_ref,
            doc: d.unsigned,
            state: 'unsigned',
            fileName: 'mhl-esign-unsigned.pdf',
        })
    }
    if (d.signed) {
        extras.push({
            key: d.signed.key || `${d.case_ref}-signed`,
            label: 'E-sign pack',
            caseRef: d.case_ref,
            doc: d.signed,
            state: 'signed',
            fileName: 'mhl-esign-signed.pdf',
        })
    }
    if (d.certificate) {
        extras.push({
            key: d.certificate.key || `${d.case_ref}-certificate`,
            label: 'E-sign certificate',
            caseRef: d.case_ref,
            doc: d.certificate,
            state: 'signed',
            fileName: 'mhl-esign-certificate.pdf',
        })
    }

    const seen = new Set<string>()
    const rows: EsignDocRow[] = []
    for (const row of [...packRows, ...extras]) {
        if (seen.has(row.key)) continue
        seen.add(row.key)
        rows.push(row)
    }
    return rows
}

const esignDocCount = (items: EsignDocs[], state?: EsignDocState) =>
    items.reduce((n, d) => n + esignDownloadRows(d).filter((row) => !state || row.state === state).length, 0)

export const MilitaryHearingLossClient = () => {
    const { clientId } = useParams<{ clientId: string }>()
    const navigate = useNavigate()
    const { isMhlAdmin, canOverturnReview } = useMhlAccess()

    const [record, setRecord] = useState<MhlClientRecord | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [notFound, setNotFound] = useState(false)
    const [sending, setSending] = useState<null | 'email' | 'sms'>(null)
    const [sendOutcome, setSendOutcome] = useState<{ email: { success: boolean } | null; sms: { success: boolean } | null }>({ email: null, sms: null })
    const [docTab, setDocTab] = useState<DocTab>('signed')

    const load = useCallback(
        async (silent = false) => {
            if (!clientId) return
            if (!silent) setLoading(true)
            setError(null)
            try {
                const res = await api({ url: '/military-hearing-loss-client-record', data: { clientId: Number(clientId) } })
                if (res.data?.success) {
                    setRecord(res.data)
                    setNotFound(false)
                } else {
                    setError(res.data?.message || 'Failed to load client')
                }
            } catch (err: any) {
                if (err?.response?.status === 404) setNotFound(true)
                else setError(err?.response?.data?.message || err?.message || 'Failed to load client')
            } finally {
                setLoading(false)
            }
        },
        [clientId],
    )

    useEffect(() => {
        void load()
    }, [load])

    useEffect(() => {
        if (!record) return
        setDocTab(esignDocCount(record.documents.esign, 'signed') > 0 ? 'signed' : 'unsigned')
    }, [record])

    // The most recent lead drives the header/summary; older leads (rare) are
    // listed below it so nothing attached to the client is hidden.
    const lead: MhlLeadRecord | null = record?.leads?.[0] || null
    const campaign: MhlCampaign | null = useMemo(() => {
        if (!record || !lead) return null
        return record.campaigns.find((c) => c.case_ref === lead.case_ref) || record.campaigns[0] || null
    }, [record, lead])
    const audiogram = useMemo(() => (record && lead ? record.documents.audiograms.find((a) => a.lead_id === lead.id) || null : null), [record, lead])
    const identityDocs = useMemo(() => {
        if (!record || !lead) return []
        return (record.documents.identity || []).filter((doc) => doc.lead_id === lead.id)
    }, [record, lead])

    const isDashboardLead = lead?.submitted_via === 'dashboard'
    const canSendEsign =
        !!lead && !!campaign && !campaign.signed && (!isDashboardLead || lead.review_status === 'approved')

    const sendEsign = (sendType: 'email' | 'sms') => {
        if (!lead || !record) return
        const recipient = sendType === 'email' ? record.client.email : record.client.mobile_number || record.client.telephone
        if (!recipient) {
            Report.warning('Missing contact detail', `Client has no ${sendType === 'email' ? 'email' : 'mobile number'} on record.`, 'Okay')
            return
        }
        Confirm.show(
            `Send e-sign link via ${sendType.toUpperCase()}?`,
            `The CFA e-sign link will be sent to ${recipient}.`,
            'Send',
            'Cancel',
            async () => {
                setSending(sendType)
                try {
                    const res = await api({ url: '/military-hearing-loss-send-esign-link', data: { case_ref: lead.case_ref, sendType } })
                    if (res.data?.success) {
                        setSendOutcome((p) => ({ ...p, [sendType]: { success: true } }))
                        Notify.success(`E-sign link sent via ${sendType.toUpperCase()}`)
                        await load(true)
                    } else {
                        setSendOutcome((p) => ({ ...p, [sendType]: { success: false } }))
                        Report.failure('Send failed', res.data?.message || 'Could not send the link', 'Okay')
                    }
                } catch (err: any) {
                    setSendOutcome((p) => ({ ...p, [sendType]: { success: false } }))
                    Report.failure('Send failed', err?.response?.data?.message || err?.message || 'Could not send the link', 'Okay')
                } finally {
                    setSending(null)
                }
            },
            () => {},
            { okButtonBackground: colors.accent, titleColor: colors.ink },
        )
    }

    // Stop / resume the drop-off chase (client opted out). Stopping asks for an
    // optional reason so the record shows why no more messages went out.
    const [chaseUpdating, setChaseUpdating] = useState(false)
    const setChaseStopped = (caseRef: string, stop: boolean) => {
        const run = async (reason?: string) => {
            setChaseUpdating(true)
            try {
                const res = await api({ url: '/military-hearing-loss-chase-stop', data: { case_ref: caseRef, stop, reason } })
                if (res.data?.success) {
                    Notify.success(stop ? 'Chase campaign stopped' : 'Chase campaign resumed')
                    await load(true)
                } else {
                    Report.failure('Update failed', res.data?.message || 'Could not update the chase campaign', 'Okay')
                }
            } catch (err: any) {
                Report.failure('Update failed', err?.response?.data?.message || err?.message || 'Could not update the chase campaign', 'Okay')
            } finally {
                setChaseUpdating(false)
            }
        }
        if (stop) {
            Confirm.prompt(
                'Stop chase campaign?',
                'No further chase emails or SMS will be sent to this client. Reason (optional):',
                'Client opted out',
                'Stop chase',
                'Cancel',
                (answer: string) => void run(answer),
                () => {},
                { okButtonBackground: colors.bad, titleColor: colors.ink },
            )
        } else {
            Confirm.show(
                'Resume chase campaign?',
                'The chase sequence will pick up where it left off and the client may receive further messages.',
                'Resume',
                'Cancel',
                () => void run(),
                () => {},
                { okButtonBackground: colors.accent, titleColor: colors.ink },
            )
        }
    }

    const [deleting, setDeleting] = useState(false)
    const deleteClient = (force = false) => {
        if (!record) return
        const clientName = fullName([record.client.firstname, record.client.lastname])
        const signed = record.campaigns.some((c) => c.signed === 1)
        Confirm.show(
            force ? 'Delete a client with a signed CFA?' : 'Delete client',
            force
                ? `${clientName} has a signed CFA. Deleting removes the client, every lead, e-sign campaign and audiogram from the MHL database. Signed PDFs stay in storage. This cannot be undone.`
                : `Permanently delete ${clientName} (client #${record.client.id}) and all attached leads, service records, e-sign campaigns and audiograms? This cannot be undone.${signed ? ' You will be asked to confirm again because a CFA has been signed.' : ''}`,
            force ? 'Yes, delete anyway' : 'Delete client',
            'Cancel',
            async () => {
                setDeleting(true)
                try {
                    const res = await api({ url: '/military-hearing-loss-client-delete', data: { clientId: record.client.id, force } })
                    if (res.data?.success) {
                        Notify.success(`Deleted ${clientName}`)
                        navigate('/military-hearing-loss/leads', { replace: true })
                        return
                    }
                    Report.failure('Delete failed', res.data?.message || 'Could not delete the client', 'Okay')
                } catch (err: any) {
                    if (err?.response?.status === 409 && err?.response?.data?.requiresForce) {
                        setDeleting(false)
                        deleteClient(true)
                        return
                    }
                    Report.failure('Delete failed', err?.response?.data?.message || err?.message || 'Could not delete the client', 'Okay')
                } finally {
                    setDeleting(false)
                }
            },
            () => {},
            { okButtonBackground: colors.bad, titleColor: colors.ink },
        )
    }

    if (loading) {
        return (
            <div className={styles.wrap}>
                <div className={styles.emptyState}>Loading client…</div>
            </div>
        )
    }

    if (notFound || (!record && !error)) {
        return (
            <div className={styles.wrap}>
                <button type="button" className={styles.backLink} onClick={() => navigate('/military-hearing-loss/leads')}>
                    <FaArrowLeft size={11} /> Back to leads
                </button>
                <div className={styles.emptyState}>Client not found.</div>
            </div>
        )
    }

    if (error || !record) {
        return (
            <div className={styles.wrap}>
                <button type="button" className={styles.backLink} onClick={() => navigate('/military-hearing-loss/leads')}>
                    <FaArrowLeft size={11} /> Back to leads
                </button>
                <div className={styles.errorBanner}>{error}</div>
            </div>
        )
    }

    const { client } = record
    const name = fullName([client.title, client.firstname, client.middle_names, client.lastname])

    return (
        <div className={styles.wrap}>
            <button type="button" className={styles.backLink} onClick={() => navigate(-1)}>
                <FaArrowLeft size={11} /> Back
            </button>

            <header className={styles.pageHeader}>
                <div>
                    <h1 className={styles.pageTitle}>{name}</h1>
                    <div className={styles.headerMeta}>
                        {lead && <span className={styles.refCode}>{lead.case_ref}</span>}
                        <span className={styles.metaText}>Client #{client.id}</span>
                        {lead && (
                            <Pill tone={isDashboardLead ? 'brand' : 'muted'}>{isDashboardLead ? 'Onboarded via dashboard' : 'Existing client base'}</Pill>
                        )}
                        {lead && isDashboardLead && (
                            <Pill tone={reviewTone(lead.review_status)} dot>
                                {reviewLabel(lead.review_status)}
                            </Pill>
                        )}
                        {campaign && (
                            <Pill tone={esignTone(campaign.signed === 1, !!campaign.link_sent_at)}>
                                {esignLabel(campaign.signed === 1, campaign.link_sent_at, campaign.link_opened_at)}
                            </Pill>
                        )}
                    </div>
                </div>
                <div className={styles.headerActions}>
                    <button type="button" className={styles.btn} onClick={() => void load(true)}>
                        <FaSyncAlt size={11} /> Refresh
                    </button>
                    {isMhlAdmin && (
                        <button type="button" className={`${styles.btn} ${styles.btnDangerGhost}`} onClick={() => deleteClient(false)} disabled={deleting} title="Permanently delete this client and everything attached">
                            <FaTrashAlt size={11} /> {deleting ? 'Deleting…' : 'Delete client'}
                        </button>
                    )}
                </div>
            </header>

            <div className={styles.factStrip}>
                <div className={styles.fact}>
                    <span className={styles.factLabel}>Audiology</span>
                    <span className={styles.factValue} title={lead?.audiology ? AUDIOLOGY_LONG_LABELS[lead.audiology] : undefined}>
                        <span className={`${styles.factDot} ${lead?.audiology ? styles[audiologyTone(lead.audiology)] : ''}`} />
                        {lead?.audiology ? AUDIOLOGY_LONG_LABELS[lead.audiology] : '—'}
                    </span>
                    {lead?.audiology_assessed_at ? (
                        <span className={styles.factNote}>{formatAssessedDb(lead.audiology_assessed_db) ? `${formatAssessedDb(lead.audiology_assessed_db)} NIHL · assessed` : 'assessed'}</span>
                    ) : lead?.audiology === 'tbc' ? (
                        <span className={styles.factNote}>awaiting assessment</span>
                    ) : lead ? (
                        <span className={styles.factNote}>reported at submission</span>
                    ) : null}
                </div>
                <div className={styles.fact}>
                    <span className={styles.factLabel}>Quantum</span>
                    <span className={styles.factValue}>{quantumLabel(lead?.quantum)}</span>
                </div>
                <div className={styles.fact}>
                    <span className={styles.factLabel}>Audiogram</span>
                    <span className={styles.factValue}>
                        <span className={`${styles.factDot} ${audiogram ? styles.good : styles.warn}`} />
                        {audiogram ? 'Attached' : 'Not uploaded'}
                    </span>
                    {audiogram?.uploadedAt && <span className={styles.factNote}>{formatDateTime(audiogram.uploadedAt)}</span>}
                </div>
                <div className={styles.fact}>
                    <span className={styles.factLabel}>Submitted</span>
                    <span className={styles.factValue}>{lead ? formatDate(lead.created_at) : '—'}</span>
                    {lead && isDashboardLead && <span className={styles.factNote}>by {agentDisplay(lead.added_by_name, lead.added_by)}</span>}
                </div>
                {campaign && (
                    <div className={styles.fact}>
                        <span className={styles.factLabel}>{campaign.signed === 1 ? 'CFA signed' : campaign.link_sent_at ? 'E-sign link sent' : 'E-sign'}</span>
                        <span className={styles.factValue}>
                            <span className={`${styles.factDot} ${campaign.signed === 1 ? styles.good : campaign.link_sent_at ? styles.info : ''}`} />
                            {campaign.signed === 1 ? formatDate(campaign.signature_date) : campaign.link_sent_at ? formatDate(campaign.link_sent_at) : 'Not sent'}
                        </span>
                        {campaign.signed !== 1 && campaign.link_opened_at && <span className={styles.factNote}>opened {formatDateTime(campaign.link_opened_at)}</span>}
                    </div>
                )}
            </div>

            <div className={styles.layout}>
                <main>
                    <ClientDetailsSection
                        client={client}
                        sourceLabel={lead ? (isDashboardLead ? 'Dashboard (agent onboarded)' : 'Public webform') : null}
                        cfaSigned={record.campaigns.some((c) => Number(c.signed) === 1)}
                        onSaved={() => load(true)}
                    />

                    {lead && (
                        <ServiceRecordsSection
                            lead={lead}
                            cfaSigned={record.campaigns.some((c) => Number(c.signed) === 1)}
                            onSaved={() => load(true)}
                        />
                    )}

                    {lead && (
                        <CollapsibleSection title="Audiogram" count={audiogram ? audiogram.fileName : 'not uploaded'} defaultOpen>
                            <AudiogramViewer
                                caseRef={lead.case_ref}
                                doc={audiogram}
                                canUpload={true}
                                onUploaded={() => load(true)}
                                reviewAside={
                                    isMhlAdmin ? <AudiologyAssessment lead={lead} hasAudiogram={!!audiogram} canAssess={isMhlAdmin} onChanged={() => load(true)} /> : undefined
                                }
                            />
                            <AudiologyAssessment lead={lead} hasAudiogram={!!audiogram} canAssess={isMhlAdmin} onChanged={() => load(true)} />
                        </CollapsibleSection>
                    )}

                    {lead && (
                        <CollapsibleSection title="Medical indicators" defaultOpen>
                            <div className={styles.columns}>
                                <dl className={styles.defList}>
                                    <dt>Audiology</dt>
                                    <dd>
                                        <Pill tone={audiologyTone(lead.audiology)}>{lead.audiology ? AUDIOLOGY_LONG_LABELS[lead.audiology] : '—'}</Pill>
                                        {lead.audiology_assessed_at && formatAssessedDb(lead.audiology_assessed_db) && (
                                            <span className={styles.stackSecondary} style={{ marginLeft: 8 }}>
                                                {formatAssessedDb(lead.audiology_assessed_db)} NIHL measured
                                            </span>
                                        )}
                                    </dd>
                                    <dt>Quantum</dt>
                                    <dd>{quantumLabel(lead.quantum)}</dd>
                                    <dt>Tinnitus</dt>
                                    <dd>{yesNo(lead.hearing_loss_or_tinnitus ?? lead.tinnitus)}</dd>
                                    <dt>Hyperacusis</dt>
                                    <dd>{yesNo(lead.hyperacusis)}</dd>
                                </dl>
                                <dl className={styles.defList}>
                                    <dt>Alt. noise exposure</dt>
                                    <dd>{lead.alternative_noise_exposure ? (lead.alternative_noise_exposure === 'some' ? 'Some' : 'None') : '—'}</dd>
                                    <dt>Civilian exposure</dt>
                                    <dd>{yesNo(lead.alternate_civilian_exposure)}</dd>
                                    <dt>AFCS / War Pension</dt>
                                    <dd>
                                        {yesNo(lead.successful_afcs_war_pension_claim)}
                                        {lead.successful_afcs_war_pension_claim ? ` · ${formatMoney(lead.afcs_award_amount)}` : ''}
                                    </dd>
                                    <dt>NFCI claim</dt>
                                    <dd>{yesNo(lead.nfci_claim)}</dd>
                                </dl>
                            </div>
                        </CollapsibleSection>
                    )}

                    <CollapsibleSection title="E-sign" count={record.campaigns.length ? `${record.campaigns.length} campaign${record.campaigns.length === 1 ? '' : 's'}` : undefined} defaultOpen>
                        {campaign ? (
                            <>
                                <div className={styles.columns}>
                                    <dl className={styles.defList}>
                                        <dt>Status</dt>
                                        <dd>
                                            <Pill tone={esignTone(campaign.signed === 1, !!campaign.link_sent_at)}>
                                                {esignLabel(campaign.signed === 1, campaign.link_sent_at, campaign.link_opened_at)}
                                            </Pill>
                                        </dd>
                                        <dt>Link sent</dt>
                                        <dd>{formatDateTime(campaign.link_sent_at)}</dd>
                                        <dt>Link opened</dt>
                                        <dd>{formatDateTime(campaign.link_opened_at)}</dd>
                                        <dt>Signed</dt>
                                        <dd>{campaign.signed === 1 ? formatDateTime(campaign.signature_date) : '—'}</dd>
                                    </dl>
                                    <dl className={styles.defList}>
                                        <dt>Sent by</dt>
                                        <dd>{agentDisplay(campaign.added_by_name, campaign.added_by)}</dd>
                                        <dt>Proceed confirmed</dt>
                                        <dd>{yesNo(campaign.proceed)}</dd>
                                        <dt>Signing IP</dt>
                                        <dd className={styles.mono}>{campaign.ip_address || '—'}</dd>
                                        <dt>Campaign status</dt>
                                        <dd>{campaign.status || '—'}</dd>
                                    </dl>
                                </div>

                                {canSendEsign && (
                                    <>
                                        <div className={styles.divider} />
                                        <div className={styles.subTitle}>{campaign.link_sent_at ? 'Re-send e-sign link' : 'Send e-sign link'}</div>
                                        <EsignSendButtons
                                            isSending={sending}
                                            emailResult={sendOutcome.email}
                                            smsResult={sendOutcome.sms}
                                            onSendEmail={() => sendEsign('email')}
                                            onSendSms={() => sendEsign('sms')}
                                            emailLabel="Send by Email"
                                            smsLabel="Send by SMS"
                                        />
                                    </>
                                )}
                                {isDashboardLead && lead?.review_status === 'awaiting_review' && (
                                    <p className={styles.hint} style={{ marginTop: 12 }}>
                                        The e-sign link is locked until this client has been approved at review.
                                    </p>
                                )}
                            </>
                        ) : (
                            <p className={styles.emptyStateSmall}>No e-sign campaign exists for this client.</p>
                        )}
                    </CollapsibleSection>

                    <CollapsibleSection
                        title="Chase messages"
                        count={
                            record.chaseMessages?.length
                                ? `${record.chaseMessages.length} sent`
                                : campaign?.num_chases
                                  ? `${campaign.num_chases} chase${campaign.num_chases === 1 ? '' : 's'}`
                                  : undefined
                        }
                        defaultOpen={!!record.chaseMessages?.length}
                    >
                        {campaign && (() => {
                            const journeyComplete = campaign.signed === 1 && (record.documents.identity?.length ?? 0) >= 2
                            const stopped = !!campaign.chase_stopped_at
                            const capped = (campaign.num_chases ?? 0) >= 5
                            const notChaseable =
                                journeyComplete || lead?.review_status === 'rejected' || (isDashboardLead && !campaign.link_sent_at)
                            const inChase = !stopped && !notChaseable && !capped
                            // Offer Stop while the client could still receive messages; Resume whenever stopped.
                            const canStop = !stopped && !notChaseable && !capped
                            const canResume = stopped
                            return (
                                <>
                                    <div className={styles.columns} style={{ marginBottom: 12 }}>
                                        <dl className={styles.defList}>
                                            <dt>Chases sent</dt>
                                            <dd>{campaign.num_chases ?? 0} of 5</dd>
                                            <dt>Last chase</dt>
                                            <dd>{formatDateTime(campaign.last_chase_date)}</dd>
                                        </dl>
                                        <dl className={styles.defList}>
                                            <dt>In chase</dt>
                                            <dd>
                                                {stopped ? (
                                                    <Pill tone="bad" dot>
                                                        Stopped
                                                    </Pill>
                                                ) : journeyComplete ? (
                                                    'No — journey complete'
                                                ) : lead?.review_status === 'rejected' ? (
                                                    'No — rejected at review'
                                                ) : isDashboardLead && !campaign.link_sent_at ? (
                                                    'No — e-sign link not sent yet'
                                                ) : capped ? (
                                                    'No — reached the 5-chase cap'
                                                ) : campaign.signed === 1 ? (
                                                    'Yes — signed, identity documents missing'
                                                ) : (
                                                    'Yes — CFA not signed'
                                                )}
                                            </dd>
                                            {stopped && (
                                                <>
                                                    <dt>Stopped</dt>
                                                    <dd>
                                                        {formatDateTime(campaign.chase_stopped_at)}
                                                        {campaign.chase_stopped_by ? ` by ${campaign.chase_stopped_by}` : ''}
                                                        {campaign.chase_stopped_reason ? ` — ${campaign.chase_stopped_reason}` : ''}
                                                    </dd>
                                                </>
                                            )}
                                        </dl>
                                    </div>

                                    {(canStop || canResume) && (
                                        <>
                                            <div className={styles.divider} />
                                            <div className={styles.subTitle}>Chase campaign</div>
                                            <div className={styles.headerActions}>
                                                {canStop && (
                                                    <button
                                                        type="button"
                                                        className={`${styles.btn} ${styles.btnDangerGhost}`}
                                                        onClick={() => setChaseStopped(campaign.case_ref, true)}
                                                        disabled={chaseUpdating}
                                                        title="Stop sending chase emails and SMS to this client"
                                                    >
                                                        <FaBan size={11} /> {chaseUpdating ? 'Updating…' : 'Stop chase campaign'}
                                                    </button>
                                                )}
                                                {canResume && (
                                                    <button
                                                        type="button"
                                                        className={styles.btn}
                                                        onClick={() => setChaseStopped(campaign.case_ref, false)}
                                                        disabled={chaseUpdating}
                                                        title="Allow chase messages to resume from where they left off"
                                                    >
                                                        <FaPlay size={10} /> {chaseUpdating ? 'Updating…' : 'Resume chase campaign'}
                                                    </button>
                                                )}
                                                <span className={styles.hint}>
                                                    {inChase
                                                        ? 'Stop if the client has opted out — the daily chase will skip them.'
                                                        : stopped
                                                          ? 'The daily chase skips this client while stopped.'
                                                          : ''}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </>
                            )
                        })()}
                        <ChaseMessagesTable
                            messages={record.chaseMessages || []}
                            hideClient
                            pageSize={10}
                            emptyMessage="No chase emails or SMS have been sent to this client."
                        />
                    </CollapsibleSection>

                    <CollapsibleSection
                        title="Documents"
                        count={record.documents.audiograms.length + esignDocCount(record.documents.esign) + identityDocs.length}
                        defaultOpen={false}
                    >
                        <SourceTabs
                            ariaLabel="Document version"
                            value={docTab}
                            onChange={setDocTab}
                            options={[
                                { value: 'unsigned', label: 'Unsigned', count: esignDocCount(record.documents.esign, 'unsigned') },
                                { value: 'signed', label: 'Signed', count: esignDocCount(record.documents.esign, 'signed') },
                                { value: 'identity', label: 'Identity Documents', count: identityDocs.length },
                                { value: 'other', label: 'Other', count: record.documents.audiograms.length },
                            ]}
                        />
                        {docTab === 'identity' ? (
                            lead ? (
                                <IdentityDocumentsSection caseRef={lead.case_ref} docs={identityDocs} hideHeading />
                            ) : (
                                <p className={styles.emptyStateSmall}>No lead is attached to this client.</p>
                            )
                        ) : docTab === 'other' ? (
                            <div className={styles.identitySlots}>
                                {record.documents.audiograms.map((a) => (
                                    <div key={a.key}>
                                        <div className={styles.subTitle}>Audiogram</div>
                                        <AudiogramViewer
                                            caseRef={a.case_ref}
                                            doc={a}
                                            canUpload={false}
                                            previewAriaLabel="Audiogram review"
                                        />
                                    </div>
                                ))}
                                {!record.documents.audiograms.length && (
                                    <p className={styles.emptyStateSmall}>No audiogram has been uploaded for this client.</p>
                                )}
                            </div>
                        ) : (
                            <div className={styles.identitySlots}>
                                {record.documents.esign.flatMap((d) =>
                                    esignDownloadRows(d)
                                        .filter((row) => row.state === docTab)
                                        .map((row) => (
                                            <div key={row.key}>
                                                <div className={styles.subTitle}>{row.label}</div>
                                                <AudiogramViewer
                                                    caseRef={row.caseRef}
                                                    doc={toPreviewDoc(row)}
                                                    canUpload={false}
                                                    previewAriaLabel={`${row.label} review`}
                                                />
                                            </div>
                                        )),
                                )}
                                {esignDocCount(record.documents.esign, docTab) === 0 && (
                                    <p className={styles.emptyStateSmall}>
                                        {esignDocCount(record.documents.esign) === 0
                                            ? 'No e-sign documents are stored for this client yet.'
                                            : `No ${docTab} documents for this client.`}
                                    </p>
                                )}
                            </div>
                        )}
                    </CollapsibleSection>

                    {record.leads.length > 1 && (
                        <CollapsibleSection title="Other leads for this client" count={record.leads.length - 1} defaultOpen={false}>
                            <ul className={styles.docList}>
                                {record.leads.slice(1).map((l) => (
                                    <li key={l.id} className={styles.docItem}>
                                        <div className={styles.stackCell}>
                                            <span className={styles.docName}>{l.case_ref}</span>
                                            <span className={styles.docMeta}>
                                                {formatDateTime(l.created_at)} · {l.submitted_via} · {reviewLabel(l.review_status)}
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </CollapsibleSection>
                    )}
                </main>

                <aside className={styles.aside}>
                    {lead && isDashboardLead && (
                        <ReviewPanel
                            lead={lead}
                            client={client}
                            campaign={campaign}
                            hasAudiogram={!!audiogram}
                            isAssessed={!!lead.audiology_assessed_at && (lead.audiology === '>=4dB' || lead.audiology === '<4dB')}
                            canReview={isMhlAdmin}
                            canOverturn={record?.can_overturn_review === true || canOverturnReview}
                            onChanged={() => load(true)}
                        />
                    )}

                    {lead && (
                        <section className={styles.panel}>
                            <h3 className={styles.panelTitle}>Submission</h3>
                            <dl className={`${styles.defList} ${styles.defListTight}`}>
                                <dt>Submitted</dt>
                                <dd>{formatDateTime(lead.created_at)}</dd>
                                <dt>Source</dt>
                                <dd>{isDashboardLead ? 'Dashboard' : 'Webform'}</dd>
                                {isDashboardLead && (
                                    <>
                                        <dt>Agent</dt>
                                        {/* Email lives in the icon tooltip — a second line wraps untidily in this narrow column. */}
                                        <dd>
                                            {agentDisplay(lead.added_by_name, lead.added_by)}
                                            {lead.added_by && lead.added_by_name?.trim() && <InfoTip text={lead.added_by} className={styles.emailTip} />}
                                        </dd>
                                        <dt>Organisation</dt>
                                        <dd>{lead.added_by_org || '—'}</dd>
                                    </>
                                )}
                                <dt>Consent (claim)</dt>
                                <dd>{yesNo(lead.consent_to_contact_claim)}</dd>
                                <dt>Consent (future)</dt>
                                <dd>{yesNo(lead.consent_to_contact_future)}</dd>
                                {lead.review_notified_at && (
                                    <>
                                        <dt>Reviewer notified</dt>
                                        <dd>{formatDateTime(lead.review_notified_at)}</dd>
                                    </>
                                )}
                            </dl>
                        </section>
                    )}

                    <section className={styles.panel}>
                        <h3 className={styles.panelTitle}>Timeline</h3>
                        {record.timeline.length ? (
                            <ul className={styles.timeline}>
                                {record.timeline.map((ev) => (
                                    <li key={ev.key} className={`${styles.timelineItem} ${timelineTone(ev.type) ? styles[timelineTone(ev.type)] : ''}`}>
                                        <span className={styles.timelineLabel}>{ev.label}</span>
                                        <span className={styles.timelineMeta}>
                                            {formatDateTime(ev.at)}
                                            {ev.detail ? ` · ${ev.detail}` : ''}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className={styles.emptyStateSmall}>No activity yet.</p>
                        )}
                    </section>
                </aside>
            </div>
        </div>
    )
}
